import { useState } from 'react';
import { Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack, TextField, MenuItem, IconButton, Tooltip, Alert } from '@mui/material';
import { Replay, PlayCircle } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { jobApi } from '../../api/integration.api';

const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  queued: 'info', running: 'warning', success: 'success', failed: 'error', dead: 'error',
};

export function FailedQueuePage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState('dead');
  const [msg, setMsg] = useState<string | null>(null);

  const { data } = useQuery({ queryKey: ['integration-jobs', status], queryFn: () => jobApi.list({ limit: 100, status: status || undefined }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['integration-jobs'] });
  const runDue = useMutation({ mutationFn: () => jobApi.runDue(), onSuccess: (r) => { setMsg(`Processed ${r.processed} job(s): ${r.success} ok, ${r.dead} dead, ${r.requeued} requeued.`); invalidate(); } });
  const retry = useMutation({ mutationFn: (id: number) => jobApi.retry(id), onSuccess: (r) => { setMsg(`Job retried → ${r.status}.`); invalidate(); } });

  const rows = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Failed Integration Queue"
        subtitle="Retry framework jobs — drain due work, inspect failures and replay dead-lettered jobs."
        action={hasPermission('integration.manage') && <Button startIcon={<PlayCircle />} variant="contained" onClick={() => runDue.mutate()}>Run Due Jobs</Button>}
      />
      {msg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert>}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} mb={2}>
            <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="queued">Queued</MenuItem><MenuItem value="dead">Dead</MenuItem><MenuItem value="failed">Failed</MenuItem><MenuItem value="success">Success</MenuItem>
            </TextField>
          </Stack>
          {rows.length === 0 ? <EmptyState title="No jobs in this state" dense /> : (
            <Table size="small">
              <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Reference</TableCell><TableCell>Attempts</TableCell><TableCell>Scheduled</TableCell><TableCell>Status</TableCell><TableCell>Last error</TableCell><TableCell align="right" /></TableRow></TableHead>
              <TableBody>
                {rows.map((j) => (
                  <TableRow key={j.id} hover>
                    <TableCell>{j.job_type}</TableCell>
                    <TableCell>{j.reference_type ? `${j.reference_type}#${j.reference_id}` : '—'}</TableCell>
                    <TableCell>{j.attempts}/{j.max_attempts}</TableCell>
                    <TableCell>{new Date(Number(j.scheduled_at)).toLocaleString()}</TableCell>
                    <TableCell><Chip size="small" color={statusColor[j.status]} label={j.status} /></TableCell>
                    <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.last_error ?? '—'}</TableCell>
                    <TableCell align="right">
                      {j.status !== 'success' && hasPermission('webhook.replay') && <Tooltip title="Retry now"><IconButton size="small" onClick={() => retry.mutate(j.id)}><Replay fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
